import { formatNumber, formatPercentage } from "@/utils/numbers";
import { getCurrentLocale } from "@/utils/locale";

export const EMPTY_VALUE = "—";

export type Tone = "positive" | "negative" | "neutral";

export const TONE_CLASSES: Record<Tone, string> = {
  positive: "text-text-positive",
  negative: "text-text-negative",
  neutral: ""
};

const isMissing = (value: number | null | undefined): value is null | undefined =>
  value == null || Number.isNaN(value);

/**
 * A compact currency amount such as "$1.2M". `signed` prefixes a "+" on gains,
 * for the net-new figures; losses always carry "-". A value that rounds to zero
 * carries no sign, so a tiny loss never reads as "-$0".
 */
export const formatMoney = (
  value: number | null | undefined,
  { signed = false, isCurrency = true }: { signed?: boolean; isCurrency?: boolean } = {}
): string => {
  if (isMissing(value)) return EMPTY_VALUE;
  const { value: digits, modifier, currencySign } = formatNumber(
    Math.abs(value),
    1,
    isCurrency
  );
  // Decided numerically: the localized digits may not be Latin. Compact notation
  // only scales values of 1,000 and up, so a value displays as zero exactly when
  // it rounds to zero at one decimal.
  const isZero = Math.round(Math.abs(value) * 10) === 0;
  const sign = isZero ? "" : value < 0 ? "-" : signed ? "+" : "";
  return `${sign}${currencySign ?? ""}${digits}${modifier}`;
};

export const formatPercent = (
  value: number | null | undefined,
  precision?: number
): string => (isMissing(value) ? EMPTY_VALUE : formatPercentage(value, precision));

/** Gains read green and losses red; anything at zero stays neutral. */
export const gainTone = (value: number | null | undefined): Tone =>
  !isMissing(value) && value > 0 ? "positive" : "neutral";

export const lossTone = (value: number | null | undefined): Tone =>
  !isMissing(value) && value > 0 ? "negative" : "neutral";

/** The 1-based fiscal quarter `date` falls in, for a fiscal year starting in `fiscalYearStartMonth` (0-indexed). */
export const fiscalQuarter = (date: Date, fiscalYearStartMonth: number): number =>
  Math.floor(((date.getMonth() - fiscalYearStartMonth + 12) % 12) / 3) + 1;

/** The fiscal year's first day as a short date, such as "Jan 1". */
export const formatFiscalYearStart = (fiscalYearStartMonth: number): string =>
  new Intl.DateTimeFormat(getCurrentLocale(), {
    month: "short",
    day: "numeric"
  }).format(new Date(2000, fiscalYearStartMonth, 1));

/** The calendar year the fiscal year containing `date` started in. */
export const fiscalYear = (date: Date, fiscalYearStartMonth: number): number =>
  date.getMonth() >= fiscalYearStartMonth
    ? date.getFullYear()
    : date.getFullYear() - 1;

/**
 * The fiscal year containing `date` as a label: "2026" when it runs January to
 * December, and "FY 26–27" when it spans two calendar years.
 */
export const fiscalYearLabel = (date: Date, fiscalYearStartMonth: number): string => {
  const start = fiscalYear(date, fiscalYearStartMonth);
  if (fiscalYearStartMonth === 0) return String(start);
  const shortYear = (year: number) => String(year % 100).padStart(2, "0");
  return `FY ${shortYear(start)}–${shortYear(start + 1)}`;
};

/** The month of `date` as a short name, such as "Oct". */
export const formatShortMonth = (date: Date): string =>
  new Intl.DateTimeFormat(getCurrentLocale(), { month: "short" }).format(date);
